import { c as _c } from "react/compiler-runtime";
import { useMemo } from 'react';
import { useTranslation } from '@/hooks/use-translation';
import type { KeyboardLike, KeyWith } from '@/lib/piano/keyboard';
import { FrontWeightLevel } from '@/lib/piano/touch-design/front-weight-level';
import type { MeasuredKeyRequirements } from '@/lib/piano/touch-design/measured-key.requirements';
import { CHART_HEIGHTS } from './chart-geometry';
import { scaleOver } from './echarts/build-base-option';
import { ChartEmptyFrame } from './figure/ChartEmptyFrame';
import { ChartFigure } from './figure/ChartFigure';
import { ChartKeyboardAxis } from './figure/ChartKeyboardAxis';
import { ChartLegend, type ChartLegendItem } from './figure/ChartLegend';
import { useChartFigure } from './hooks/use-chart-figure';
import { useFrontWeightStandardSeries } from './hooks/use-front-weight-standard-series';
import { useGenerateSerie } from './hooks/use-generate-serie';
import { KeyboardChart, TouchDesignSerieVariant } from './KeyboardChart';
import { serieValues, type ChartReadingContext, type TouchDesignSerie } from './interfaces';
import { legendIdOf, useLegendToggles } from './hooks/use-legend-toggles';

/** The scale of the mockups: −10 g to 45 g, ruled every 10 g. */
const FRONT_WEIGHT_SCALE = {
  interval: 10,
  max: 45,
  min: -10,
  unit: 'g'
};
type KeyWithFrontWeight<T> = T & Pick<MeasuredKeyRequirements, 'frontWeight'>;
export interface FrontWeightChartProps<T> {
  /** Said over the empty figure, until there is something to draw. */
  emptyState?: React.ReactNode;
  defaultFrontWeightLevelsToInclude?: FrontWeightLevel[];
  /** Legend entries contributed by the page, e.g. the design target. */
  extraLegendItems?: ChartLegendItem[];
  keyboard: KeyboardLike<KeyWith<KeyWithFrontWeight<T>>>;
  /**
   * The reading strip at the head of the figure. It is a function so that the
   * strip reads the very series the chart draws, rather than computing them
   * again.
   */
  renderReading?: (context: ChartReadingContext) => React.ReactNode;
  targetSerie?: TouchDesignSerie;
  variant?: 'analyze' | 'design';
}
export const FrontWeightChart = t0 => {
  const $ = _c(63);
  const {
    emptyState,
    defaultFrontWeightLevelsToInclude,
    extraLegendItems,
    keyboard,
    renderReading,
    targetSerie,
    variant: t1
  } = t0;
  const variant = t1 === undefined ? "analyze" : t1;
  const {
    t
  } = useTranslation();
  const {
    geometry,
    hover,
    keys
  } = useChartFigure(keyboard);
  const {
    isSerieEmpty,
    generateSerie
  } = useGenerateSerie(keyboard);
  let t2;
  if ($[0] !== generateSerie || $[1] !== t) {
    t2 = generateSerie(_temp, t("Measured front weight"), TouchDesignSerieVariant.Measured);
    $[0] = generateSerie;
    $[1] = t;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const measuredSerie = t2;
  let t3;
  if ($[3] !== isSerieEmpty || $[4] !== measuredSerie) {
    t3 = isSerieEmpty(measuredSerie);
    $[3] = isSerieEmpty;
    $[4] = measuredSerie;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const isEmpty = t3;
  let t4;
  if ($[6] !== defaultFrontWeightLevelsToInclude) {
    t4 = defaultFrontWeightLevelsToInclude ?? [FrontWeightLevel.Level5, FrontWeightLevel.Level6, FrontWeightLevel.Level7, FrontWeightLevel.Level8, FrontWeightLevel.Level9];
    $[6] = defaultFrontWeightLevelsToInclude;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const defaultSeriesToInclude = t4;
  const standardSeries = useFrontWeightStandardSeries(keyboard, defaultSeriesToInclude);
  let t5;
  if ($[8] !== targetSerie) {
    t5 = targetSerie ? [targetSerie] : [];
    $[8] = targetSerie;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== isEmpty || $[11] !== measuredSerie) {
    t6 = isEmpty ? [] : [measuredSerie];
    $[10] = isEmpty;
    $[11] = measuredSerie;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== standardSeries || $[14] !== t5 || $[15] !== t6) {
    t7 = [...standardSeries, ...t5, ...t6].filter(_temp2);
    $[13] = standardSeries;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const series = t7;
  let t8;
  if ($[17] !== series) {
    t8 = scaleOver(FRONT_WEIGHT_SCALE, serieValues(series));
    $[17] = series;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const scale = t8;
  const {
    isShown,
    withToggles
  } = useLegendToggles();
  let t9;
  if ($[19] !== isShown || $[20] !== series) {
    t9 = series.filter(serie_0 => isShown(legendIdOf(serie_0)));
    $[19] = isShown;
    $[20] = series;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  const drawnSeries = t9;
  let t10;
  if ($[22] !== extraLegendItems || $[23] !== isEmpty || $[24] !== t || $[25] !== withToggles) {
    t10 = withToggles([...(extraLegendItems ?? []), ...(isEmpty ? [] : [{
      colorClassName: "text-foreground",
      id: "measured",
      label: t("Measured front weight"),
      mark: "dot" as const
    }]), {
      colorClassName: "text-ptl-series-standard",
      id: "standard-bold",
      label: t("Standard curves FW#5, FW#7, FW#9"),
      mark: "line" as const
    }, {
      colorClassName: "text-ptl-series-standard-soft",
      id: "standard-soft",
      label: t("FW#6, FW#8"),
      mark: "line-soft" as const
    }]);
    $[22] = extraLegendItems;
    $[23] = isEmpty;
    $[24] = t;
    $[25] = withToggles;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  const legendItems = t10;
  if (isEmpty && emptyState) {
    const t11 = variant === "analyze" ? CHART_HEIGHTS.plot : CHART_HEIGHTS.plotCompact;
    let t12;
    if ($[27] !== emptyState || $[28] !== geometry || $[29] !== keys || $[30] !== t11 || $[31] !== variant) {
      t12 = <ChartEmptyFrame geometry={geometry} height={t11} keys={keys} scale={FRONT_WEIGHT_SCALE} variant={variant}>{emptyState}</ChartEmptyFrame>;
      $[27] = emptyState;
      $[28] = geometry;
      $[29] = keys;
      $[30] = t11;
      $[31] = variant;
      $[32] = t12;
    } else {
      t12 = $[32];
    }
    return t12;
  }
  let t11;
  if ($[33] !== hover.hoveredKey || $[34] !== measuredSerie || $[35] !== renderReading || $[36] !== standardSeries) {
    t11 = renderReading?.({
      hoveredKey: hover.hoveredKey,
      measuredSerie,
      referenceSeries: standardSeries
    });
    $[33] = hover.hoveredKey;
    $[34] = measuredSerie;
    $[35] = renderReading;
    $[36] = standardSeries;
    $[37] = t11;
  } else {
    t11 = $[37];
  }
  const t12 = hover.hoveredKey;
  let t13;
  if ($[38] !== legendItems) {
    t13 = <ChartLegend items={legendItems} />;
    $[38] = legendItems;
    $[39] = t13;
  } else {
    t13 = $[39];
  }
  const t14 = hover.hoveredKey;
  const t15 = hover.setHoveredKey;
  let t16;
  if ($[40] !== t) {
    t16 = t("Keyboard: hover a key to read its measurement");
    $[40] = t;
    $[41] = t16;
  } else {
    t16 = $[41];
  }
  let t17;
  if ($[42] !== hover.hoveredKey || $[43] !== hover.setHoveredKey || $[44] !== keys || $[45] !== t16) {
    t17 = <ChartKeyboardAxis keys={keys} highlightedKey={t14} onKeyHover={t15} description={t16} />;
    $[42] = hover.hoveredKey;
    $[43] = hover.setHoveredKey;
    $[44] = keys;
    $[45] = t16;
    $[46] = t17;
  } else {
    t17 = $[46];
  }
  const t18 = variant === "analyze" ? CHART_HEIGHTS.plot : CHART_HEIGHTS.plotCompact;
  let t19;
  if ($[47] !== drawnSeries || $[48] !== geometry || $[49] !== hover.bindChart || $[50] !== scale || $[51] !== t18 || $[52] !== variant) {
    t19 = <KeyboardChart variant={variant} geometry={geometry} height={t18} onChartReady={hover.bindChart} ruleAt={0} scale={scale} series={drawnSeries} />;
    $[47] = drawnSeries;
    $[48] = geometry;
    $[49] = hover.bindChart;
    $[50] = scale;
    $[51] = t18;
    $[52] = variant;
    $[53] = t19;
  } else {
    t19 = $[53];
  }
  let t20;
  if ($[54] !== geometry || $[55] !== hover.figureProps || $[56] !== hover.hoveredKey || $[57] !== t11 || $[58] !== t13 || $[59] !== t17 || $[60] !== t19 || $[61] !== variant) {
    t20 = <ChartFigure reading={t11} variant={variant} geometry={geometry} highlightedKey={t12} header={t13} keyboard={t17} {...hover.figureProps}>{t19}</ChartFigure>;
    $[54] = geometry;
    $[55] = hover.figureProps;
    $[56] = hover.hoveredKey;
    $[57] = t11;
    $[58] = t13;
    $[59] = t17;
    $[60] = t19;
    $[61] = variant;
    $[62] = t20;
  } else {
    t20 = $[62];
  }
  const figure = t20;
  return figure;
};
function _temp(key) {
  return key.payload.frontWeight;
}
function _temp2(serie) {
  return serie !== undefined;
}